import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Button, Alert, IconButton,
  Snackbar, CircularProgress, Tooltip
} from '@mui/material';
import {
  Add, Edit, Delete, Water as WaterIcon, CloudUpload, OpenInNew,
  Terrain, WaterDrop, Satellite, Map as MapIcon,
  Notifications, Favorite, Warning as WarningIcon
} from '@mui/icons-material';
import { dashboardAPI, pondsAPI, farmsAPI, documentsAPI, stockingEventsAPI, alertsAPI } from 'services/api';
import { useNavigate, useLocation } from 'react-router-dom';

import PondImagePopup from 'shared/components/PondImagePopup';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';

// ============================================================================
// POND CARD COMPONENT (Grid item)
// ============================================================================

const SPECIES_COLORS = {
  vannamei: { bg: '#E3F2FD', text: '#1565C0' },
  monodon: { bg: '#E8F5E9', text: '#2E7D32' },
  scampi:  { bg: '#FFF3E0', text: '#E65100' },
  indicus: { bg: '#F3E5F5', text: '#7B1FA2' },
};

const PondCard = ({ pond, farm, stockingEvent, targetDOC, alertCount = 0, onView, onEdit, onDelete, onImagePopup }) => {
  const getStatusColor = (status) => {
    const map = {
      active: '#4CAF50', in_crop: '#1976D2', crop_out: '#00BCD4',
      maintenance: '#FF9800', inactive: '#F44336'
    };
    return map[status?.toLowerCase()] || '#9E9E9E';
  };

  const getStatusLabel = (status) => {
    const map = {
      active: 'Ready', in_crop: 'Active Crop', crop_out: 'Harvested',
      maintenance: 'Maintenance', inactive: 'Inactive'
    };
    return map[status?.toLowerCase()] || status || 'Unknown';
  };

  const getSpeciesDisplayName = (species) => {
    const map = { vannamei: 'Vannamei', monodon: 'Monodon', scampi: 'Scampi', indicus: 'Indicus' };
    return map[species?.toLowerCase()] || species;
  };

  const statusColor = getStatusColor(pond.status);
  const speciesKey = pond.species?.toLowerCase() || 'vannamei';
  const speciesColor = SPECIES_COLORS[speciesKey] || { bg: '#F5F5F5', text: '#616161' };

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 cursor-pointer p-6 flex flex-col justify-between min-h-[240px]"
      onClick={() => onView(pond)}
    >
      {/* Top section */}
      <div className="space-y-3">
        {/* Row 1: Pond Name + Status badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {pond.pond_name}
            </h3>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {farm?.name || 'Unknown Farm'}
            </p>
          </div>
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0 leading-none"
            style={{
              backgroundColor: `${statusColor}14`,
              color: statusColor,
              border: `1px solid ${statusColor}30`
            }}
          >
            {getStatusLabel(pond.status)}
          </span>
        </div>

        {/* Row 2: Species badge + Alerts count */}
        <div className="flex items-center justify-between">
          <span
            className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold leading-none"
            style={{ backgroundColor: speciesColor.bg, color: speciesColor.text }}
          >
            🦐 {getSpeciesDisplayName(pond.species)}
          </span>
          {alertCount > 0 ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              <WarningIcon sx={{ fontSize: 12 }} />
              {alertCount}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-gray-400 bg-gray-50">
              ✓ No alerts
            </span>
          )}
        </div>

        {/* Row 3: Area + Density */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 rounded-xl p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Area</p>
            <p className="text-sm font-bold text-gray-800 mt-0.5">
              {pond.pond_area ? parseFloat(pond.pond_area).toFixed(2) : '—'}
              <span className="text-xs font-normal text-gray-500 ml-0.5">ha</span>
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Density</p>
            <p className="text-sm font-bold text-gray-800 mt-0.5">
              {pond.stocking_density ? parseFloat(pond.stocking_density).toFixed(1) : '—'}
              <span className="text-xs font-normal text-gray-500 ml-0.5">/m²</span>
            </p>
          </div>
        </div>

        {/* Row 4: DOC small progress bar */}
        {stockingEvent && stockingEvent.stocking_date && (
          <div className="bg-blue-50/60 rounded-xl p-2.5 border border-blue-100">
            <DOCProgressIndicator
              stockingDate={stockingEvent.stocking_date}
              targetDOC={targetDOC}
              variant="compact"
              size="small"
              showDetails={false}
            />
          </div>
        )}
      </div>

      {/* Bottom actions — pinned */}
      <div className="flex items-center justify-between pt-3 mt-auto border-t border-gray-100">
        <div className="flex items-center gap-1">
          {pond.latitude && pond.longitude && (
            <Tooltip title="View on Map">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps?q=${pond.latitude},${pond.longitude}`, '_blank'); }}
                sx={{ color: '#4285F4', p: 0.5 }}
              >
                <MapIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Satellite View">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onImagePopup(pond); }}
              sx={{ color: '#4CAF50', p: 0.5 }}
            >
              <Satellite sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(pond); }}
              sx={{ color: '#1976D2', p: 0.5 }}
            >
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(pond.id); }}
              sx={{ color: '#F44336', p: 0.5 }}
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ADD NEW POND CARD
// ============================================================================

const AddPondCard = ({ onClick, disabled }) => (
  <div
    className={`border-2 border-dashed rounded-2xl flex items-center justify-center transition cursor-pointer min-h-[240px] ${
      disabled
        ? 'border-gray-200 cursor-not-allowed opacity-50'
        : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
    }`}
    onClick={disabled ? undefined : onClick}
  >
    <div className="flex flex-col items-center">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${disabled ? 'bg-gray-100' : 'bg-blue-100'}`}>
        <Add sx={{ fontSize: 24, color: disabled ? '#9E9E9E' : '#1976D2' }} />
      </div>
      <p className={`text-sm font-semibold ${disabled ? 'text-gray-400' : 'text-blue-600'}`}>
        Add New Pond
      </p>
      <p className="text-xs text-gray-400 mt-1">
        {disabled ? 'Register a farm first' : 'Click to register'}
      </p>
    </div>
  </div>
);

// ============================================================================
// FARM-LEVEL SUMMARY CARD
// ============================================================================

const SummaryCard = ({ label, value, color, sub, icon }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col justify-center min-h-[110px]">
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}15`, color }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">
          {label}
        </p>
        <p className="text-lg font-bold mt-0.5" style={{ color }}>
          {value}
        </p>
        {sub && (
          <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// MAIN PONDS OVERVIEW PAGE
// ============================================================================

const PondsOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [ponds, setPonds] = useState([]);
  const [farms, setFarms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPond, setEditingPond] = useState(null);
  const [openImagePopup, setOpenImagePopup] = useState(false);
  const [selectedPondForImage, setSelectedPondForImage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [allStockingEvents, setAllStockingEvents] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [pondAlertCounts, setPondAlertCounts] = useState({});
  const [formData, setFormData] = useState({
    farm_id: '', pond_name: '', pond_area: '', depth: '',
    stocking_density: '', species: 'Vannamei', stocking_date: '',
    doc_requested: 120, status: 'active',
  });

  const scrollRestoredRef = useRef(false);

  useEffect(() => { loadData(); }, []);

  // Restore scroll position when data finishes loading
  useEffect(() => {
    if (!loading && ponds.length > 0 && !scrollRestoredRef.current) {
      scrollRestoredRef.current = true;
      const savedY = sessionStorage.getItem('pondsOverviewScrollY');
      if (savedY) {
        requestAnimationFrame(() => {
          window.scrollTo(0, parseInt(savedY, 10));
        });
        sessionStorage.removeItem('pondsOverviewScrollY');
      }
    }
  }, [loading, ponds.length]);

  // Handle URL-driven actions: ?action=add  OR  ?action=edit&pondId=123
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const action = urlParams.get('action');
    if (action === 'add' && farms.length > 0) {
      setOpenDialog(true);
      navigate('/farmer/ponds', { replace: true });
    } else if (action === 'edit') {
      const targetId = parseInt(urlParams.get('pondId'));
      if (targetId && ponds.length > 0) {
        const targetPond = ponds.find(p => p.id === targetId);
        if (targetPond) {
          handleOpenDialog(targetPond);
        }
        navigate('/farmer/ponds', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, farms.length, ponds.length]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      try {
        const bulkDataResponse = await dashboardAPI.getFarmerBulkData();
        const bulkData = bulkDataResponse.data;
        setFarms(bulkData.farms);
        const allPonds = bulkData.farms.flatMap(farm => farm.ponds || []);
        setPonds(allPonds);
      } catch (bulkError) {
        console.warn('Bulk data endpoint failed, using fallback:', bulkError);
        const farmsResponse = await farmsAPI.getAll();
        const farmsData = farmsResponse.data;
        setFarms(farmsData);
        const allPonds = [];
        for (const farm of farmsData) {
          try {
            const pondResponse = await pondsAPI.getByFarm(farm.id);
            allPonds.push(...pondResponse.data);
          } catch (pondError) { console.warn(`Failed to load ponds for farm ${farm.id}:`, pondError); }
        }
        setPonds(allPonds);
      }
      try {
        const eventsResponse = await stockingEventsAPI.getAll();
        setAllStockingEvents(eventsResponse.data || []);
      } catch (err) { console.warn('Error loading stocking events:', err); setAllStockingEvents([]); }
      try {
        const alertCountResponse = await alertsAPI.getCount();
        setAlertCount(alertCountResponse.data?.unread_count || 0);
      } catch (err) { console.warn('Error loading alert count:', err); setAlertCount(0); }
      try {
        const unackRes = await alertsAPI.getAll({ acknowledged: false, limit: 200 });
        const alerts = unackRes.data || [];
        const counts = {};
        alerts.forEach(a => { if (a.pond_id) counts[a.pond_id] = (counts[a.pond_id] || 0) + 1; });
        setPondAlertCounts(counts);
      } catch (err) { console.warn('Error loading per-pond alerts:', err); setPondAlertCounts({}); }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPondStockingEvent = (pondId) => {
    const pondEvents = allStockingEvents
      .filter(event => event.pond_id === pondId)
      .sort((a, b) => new Date(b.stocking_date) - new Date(a.stocking_date));
    return pondEvents[0] || null;
  };

  const getPondTargetDOC = (pond) => pond.doc_requested || 120;

  // ---------- FORM HANDLERS ----------

  const handleOpenDialog = (pond = null) => {
    if (pond) {
      setEditingPond(pond);
      const maxDocLimits = { Vannamei: 120, Indicus: 120, Monodon: 130, Scampi: 130 };
      const species = pond.species || 'Vannamei';
      const maxDoc = maxDocLimits[species] || 120;
      const correctedDoc = pond.doc_requested && pond.doc_requested > maxDoc ? maxDoc : (pond.doc_requested || 120);
      if (pond.doc_requested && pond.doc_requested > maxDoc) {
        setError(`DOC was automatically corrected from ${pond.doc_requested} to ${maxDoc} days (maximum for ${species})`);
      }
      setFormData({
        farm_id: pond.farm_id || '', pond_name: pond.pond_name || '',
        pond_area: pond.pond_area || '', depth: pond.depth || '',
        stocking_density: pond.stocking_density || '', species,
        stocking_date: pond.stocking_date ? pond.stocking_date.split('T')[0] : '',
        doc_requested: correctedDoc, status: pond.status || 'active',
        latitude: pond.latitude || '', longitude: pond.longitude || '',
        geospatial_image_url: pond.geospatial_image_url || '',
        water_source_type: pond.water_source_type || '', soil_type: pond.soil_type || '',
      });
    } else {
      setEditingPond(null);
      setFormData({
        farm_id: '', pond_name: '', pond_area: '', depth: '',
        stocking_density: '', species: 'Vannamei', stocking_date: '',
        doc_requested: 120, status: 'active',
        latitude: '', longitude: '', geospatial_image_url: '',
        water_source_type: '', soil_type: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPond(null);
    setError(null);
    setFieldErrors({});
    setFormData({
      farm_id: '', pond_name: '', pond_area: '', depth: '',
      stocking_density: '', species: 'Vannamei', stocking_date: '',
      doc_requested: 120, status: 'active',
    });
  };

  const validateForm = () => {
    if (!formData.farm_id) { setError('Please select a farm'); return false; }
    if (!formData.pond_name.trim()) { setError('Pond name is required'); return false; }
    if (!formData.pond_area || parseFloat(formData.pond_area) <= 0) { setError('Valid pond area is required'); return false; }
    const selectedFarm = farms.find(f => f.id === parseInt(formData.farm_id));
    if (selectedFarm && parseFloat(formData.pond_area) > selectedFarm.water_spread_area) {
      setError(`Pond area (${formData.pond_area} ha) cannot exceed farm water spread area (${selectedFarm.water_spread_area} ha)`);
      return false;
    }
    if (!formData.depth || parseFloat(formData.depth) <= 0) { setError('Valid depth is required'); return false; }
    if (!formData.stocking_density || parseFloat(formData.stocking_density) <= 0) { setError('Valid stocking density is required'); return false; }
    if (!formData.stocking_date) { setError('Stocking date is required'); return false; }
    const maxDocLimits = { Vannamei: 120, Indicus: 120, Monodon: 130, Scampi: 130 };
    const maxDoc = maxDocLimits[formData.species] || 120;
    if (formData.doc_requested && parseInt(formData.doc_requested) > maxDoc) {
      setError(`DOC (${formData.doc_requested}) exceeds maximum ${maxDoc} days for ${formData.species} species`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setError(null);
      setFieldErrors({});
      const pondData = {
        farm_id: parseInt(formData.farm_id),
        pond_name: formData.pond_name.trim(),
        pond_area: parseFloat(formData.pond_area),
        depth: parseFloat(formData.depth),
        stocking_density: parseFloat(formData.stocking_density),
        species: formData.species,
        stocking_date: formData.stocking_date + 'T00:00:00',
        doc_requested: parseInt(formData.doc_requested),
        status: formData.status,
      };
      if (editingPond) {
        await pondsAPI.update(editingPond.id, pondData);
      } else {
        await pondsAPI.create(pondData);
      }
      handleCloseDialog();
      loadData();
    } catch (err) {
      console.error('Error saving pond:', err);
      if (err.response && err.response.status === 422) {
        const errorData = err.response.data;
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            const errorDetail = errorData.detail;
            if (errorDetail.includes('Pond name')) setFieldErrors({ pond_name: errorDetail });
            else if (errorDetail.includes('Pond area')) setFieldErrors({ pond_area: errorDetail });
            else if (errorDetail.includes('Stocking density')) setFieldErrors({ stocking_density: errorDetail });
            else if (errorDetail.includes('DOC')) setFieldErrors({ doc_requested: errorDetail });
            else setError(errorDetail);
          } else if (Array.isArray(errorData.detail)) {
            const fieldMap = {};
            errorData.detail.forEach(error => {
              if (error.loc && error.loc.length > 0) {
                const fieldName = error.loc[error.loc.length - 1];
                fieldMap[fieldName] = error.msg || 'Invalid value';
              }
            });
            if (Object.keys(fieldMap).length > 0) setFieldErrors(fieldMap);
            else setError('Validation error occurred. Please check your data.');
          } else { setError('Validation error occurred. Please check your data.'); }
        } else { setError('Validation error occurred. Please check your data.'); }
      } else if (err.response?.status === 404) { setError('Pond not found. Please refresh and try again.'); }
      else if (err.response?.status === 403) { setError('Access denied. You can only edit your own ponds.'); }
      else { setError('Failed to save pond. Please check your data and try again.'); }
    }
  };

  const handleDelete = async (pondId) => {
    if (window.confirm('Are you sure you want to delete this pond?')) {
      try {
        await pondsAPI.delete(pondId);
        loadData();
      } catch (err) {
        console.error('Error deleting pond:', err);
        setError('Failed to delete pond. Please try again.');
      }
    }
  };

  const handleViewPond = (pond) => {
    sessionStorage.setItem('pondsOverviewScrollY', String(window.scrollY));
    navigate(`/farmer/ponds/${pond.id}`);
  };

  const handleGeospatialImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) { setError('Please select a valid image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image file size must be less than 10MB.'); return; }
    try {
      setError(null);
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'geospatial_image');
      fd.append('description', 'Geospatial/Satellite image for pond');
      const response = await documentsAPI.uploadGeospatial(fd);
      if (response.data && response.data.file_url) {
        setFormData(prev => ({ ...prev, geospatial_image_url: response.data.file_url }));
        setSnackbar({ open: true, message: 'Image uploaded successfully!', severity: 'success' });
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Failed to upload image: ${err.response?.data?.detail || err.message || 'Please try again.'}`);
    }
  };

  // ---------- COMPUTED VALUES ----------

  const totalArea = ponds.reduce((sum, p) => sum + (p.pond_area || 0), 0);
  const inCropCount = ponds.filter(p => p.status === 'in_crop').length;
  const avgDensity = ponds.length > 0
    ? (ponds.reduce((s, p) => s + (p.stocking_density || 0), 0) / ponds.length).toFixed(1)
    : 0;
  const inactiveCount = ponds.filter(p => p.status === 'inactive').length;

  // ---------- LOADING STATE ----------

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  // ---------- RENDER ----------

  return (
    <div className="bg-gray-50 min-h-screen px-6 py-6 space-y-6 max-w-screen-2xl mx-auto page-transition-enter">

      {/* Farm Registration Warning */}
      {farms.length === 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/farmer/farms?action=add')}>
              Register Farm
            </Button>
          }
          sx={{ borderRadius: 2 }}
        >
          <strong>No Farms Registered!</strong> You need to register at least one farm before you can add ponds.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ========== ROW 1: FARM SUMMARY CARDS ========== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Ponds"
          value={ponds.length}
          color={inactiveCount > 0 ? '#F44336' : '#4CAF50'}
          sub={`${inCropCount} active crop${inCropCount !== 1 ? 's' : ''}`}
          icon={<Favorite sx={{ fontSize: 18 }} />}
        />
        <SummaryCard
          label="Total Area"
          value={`${totalArea.toFixed(1)} ha`}
          color="#1976D2"
          sub={`Across ${farms.length} farm${farms.length !== 1 ? 's' : ''}`}
          icon={<Terrain sx={{ fontSize: 18 }} />}
        />
        <SummaryCard
          label="Avg Density"
          value={`${avgDensity}/m²`}
          color="#9C27B0"
          sub="Stocking density"
          icon={<WaterDrop sx={{ fontSize: 18 }} />}
        />
        <SummaryCard
          label="Alerts"
          value={alertCount > 0 ? `${alertCount} Pending` : 'All Clear'}
          color={alertCount > 0 ? '#FF9800' : '#4CAF50'}
          sub={alertCount > 0 ? 'Review needed' : 'No action needed'}
          icon={<Notifications sx={{ fontSize: 18 }} />}
        />
      </div>

      {/* ========== ROW 2: POND CARDS GRID ========== */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            Your Ponds
          </h2>
          <span className="text-xs text-gray-400">
            {ponds.length} pond{ponds.length !== 1 ? 's' : ''} registered
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {ponds.map((pond) => {
            const farm = farms.find(f => f.id === pond.farm_id);
            const stockingEvent = getPondStockingEvent(pond.id);
            return (
              <PondCard
                key={pond.id}
                pond={pond}
                farm={farm}
                stockingEvent={stockingEvent}
                targetDOC={getPondTargetDOC(pond)}
                alertCount={pondAlertCounts[pond.id] || 0}
                onView={handleViewPond}
                onEdit={handleOpenDialog}
                onDelete={handleDelete}
                onImagePopup={(p) => { setSelectedPondForImage(p); setOpenImagePopup(true); }}
              />
            );
          })}

          {/* Add New Pond Card */}
          <AddPondCard
            onClick={() => handleOpenDialog()}
            disabled={farms.length === 0}
          />
        </div>
      </div>

      {/* ========== ADD / EDIT POND DIALOG ========== */}
      {/* MUI Dialog is kept — only Buttons, Dialogs, Icons, and Form controls use MUI */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'white' }}>
          <div className="flex items-center gap-2">
            <WaterIcon />
            <span className="text-lg font-semibold">
              {editingPond ? 'Edit Pond' : 'Register New Pond'}
            </span>
          </div>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <div className="flex flex-col gap-5">
              {/* Farm select */}
              <FormControl fullWidth required>
                <InputLabel>Select Farm</InputLabel>
                <Select
                  value={formData.farm_id}
                  onChange={(e) => setFormData({ ...formData, farm_id: e.target.value })}
                  label="Select Farm"
                  error={!formData.farm_id}
                >
                  {farms.map((farm) => (
                    <MenuItem key={farm.id} value={farm.id}>
                      <div>
                        <p className="text-sm font-medium">{farm.name}</p>
                        <p className="text-xs text-gray-500">Max area: {farm.water_spread_area} ha</p>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Pond Name + Status */}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth label="Pond Name" value={formData.pond_name}
                  onChange={(e) => setFormData({ ...formData, pond_name: e.target.value })}
                  placeholder="e.g., Pond A1" required
                  error={!!fieldErrors.pond_name} helperText={fieldErrors.pond_name || ""}
                />
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status" disabled={formData.status === 'in_crop'}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="in_crop" disabled={formData.status !== 'in_crop'}>In Crop {formData.status === 'in_crop' && '(LOCKED)'}</MenuItem>
                    <MenuItem value="crop_out">Crop Out</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Area + Depth */}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth label="Area (hectares)" type="number" value={formData.pond_area}
                  onChange={(e) => setFormData({ ...formData, pond_area: e.target.value })}
                  helperText={fieldErrors.pond_area || (formData.farm_id && farms.find(f => f.id === parseInt(formData.farm_id)) ? `Max: ${farms.find(f => f.id === parseInt(formData.farm_id)).water_spread_area} ha` : '')}
                  error={!!fieldErrors.pond_area} required
                />
                <TextField
                  fullWidth label="Depth (meters)" type="number" value={formData.depth}
                  onChange={(e) => setFormData({ ...formData, depth: e.target.value })} required
                />
              </div>

              {/* Species + Density */}
              <div className="grid grid-cols-2 gap-4">
                <FormControl fullWidth required error={!!fieldErrors.species}>
                  <InputLabel>Species</InputLabel>
                  <Select value={formData.species} onChange={(e) => setFormData({ ...formData, species: e.target.value })} label="Species">
                    <MenuItem value="Vannamei">Vannamei</MenuItem>
                    <MenuItem value="Monodon">Monodon</MenuItem>
                    <MenuItem value="Scampi">Scampi</MenuItem>
                    <MenuItem value="Indicus">Indicus</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth label="Stocking Density (per m²)" type="number" value={formData.stocking_density}
                  onChange={(e) => setFormData({ ...formData, stocking_density: e.target.value })}
                  required error={!!fieldErrors.stocking_density} helperText={fieldErrors.stocking_density || ""}
                />
              </div>

              {/* Stocking Date + DOC */}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth label="Stocking Date" type="date" value={formData.stocking_date}
                  onChange={(e) => setFormData({ ...formData, stocking_date: e.target.value })}
                  InputLabelProps={{ shrink: true }} required
                  error={!!fieldErrors.stocking_date} helperText={fieldErrors.stocking_date || ""}
                />
                <TextField
                  fullWidth label="Culture Days (DOC)" type="number" value={formData.doc_requested}
                  onChange={(e) => setFormData({ ...formData, doc_requested: e.target.value })}
                  helperText={fieldErrors.doc_requested || "90-150 days typical"} error={!!fieldErrors.doc_requested}
                />
              </div>

              {/* Lat + Long */}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth label="Latitude" type="number" value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="e.g., 20.2961" helperText="GPS latitude"
                />
                <TextField
                  fullWidth label="Longitude" type="number" value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="e.g., 85.8245" helperText="GPS longitude"
                />
              </div>

              {/* Water Source + Soil */}
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth label="Water Source Type" value={formData.water_source_type}
                  onChange={(e) => setFormData({ ...formData, water_source_type: e.target.value })}
                  placeholder="e.g., Borewell, Canal"
                />
                <TextField
                  fullWidth label="Soil Type" value={formData.soil_type}
                  onChange={(e) => setFormData({ ...formData, soil_type: e.target.value })}
                  placeholder="e.g., Clay, Sandy"
                />
              </div>

              {/* Geo image URL */}
              <TextField
                fullWidth label="Geospatial/Satellite Image URL" value={formData.geospatial_image_url}
                onChange={(e) => setFormData({ ...formData, geospatial_image_url: e.target.value })}
                placeholder="https://..." helperText="Optional: Satellite image link"
              />

              {/* Upload button */}
              <div className="flex items-center gap-4">
                <input accept="image/*" style={{ display: 'none' }} id="geospatial-image-upload" type="file" onChange={handleGeospatialImageUpload} />
                <label htmlFor="geospatial-image-upload">
                  <Button variant="outlined" component="span" startIcon={<CloudUpload />}>Upload Image</Button>
                </label>
                {formData.geospatial_image_url && (
                  <Button variant="text" size="small" onClick={() => window.open(formData.geospatial_image_url, '_blank')} startIcon={<OpenInNew />}>Preview</Button>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Button onClick={handleCloseDialog} variant="outlined" sx={{ minWidth: 100 }}>Cancel</Button>
            <Button type="submit" variant="contained" sx={{ minWidth: 120 }} disabled={!formData.farm_id || !formData.pond_name}>
              {editingPond ? 'Update' : 'Register'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Pond Image Popup */}
      <PondImagePopup
        open={openImagePopup}
        onClose={() => { setOpenImagePopup(false); setSelectedPondForImage(null); }}
        pond={selectedPondForImage}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </div>
  );
};

export default PondsOverview;
